import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';

export type FilterableSelectorOption = {
  label?: string;
  labelLines?: string[];
  value: string;
  matcher: string;
};

interface Props {
  placeholder: string;
  options: FilterableSelectorOption[];
  selected?: FilterableSelectorOption;
  onSelect: (option: FilterableSelectorOption | null) => void;
  onChange?: (query: string) => void;
  isRequired?: boolean;
  workflowFormWidth: string;
}

interface HTMLElementEvent<T extends EventTarget> extends Event {
  target: T;
}

const SelectProjects: React.FC<Props> = ({
  placeholder,
  options,
  selected,
  onSelect,
  onChange,
  isRequired,
  workflowFormWidth
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickEvent = (e: Event) => {
      const event = e as HTMLElementEvent<HTMLElement>;
      if (selectAreaRef.current && !selectAreaRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickEvent);

    return () => {
      document.removeEventListener('click', handleClickEvent);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const el = document.getElementById('filterable-selector-input');
      if (el) {
        el.focus();
      }
    }
  }, [isOpen]);

  const getFilteredOptions = () => {
    return options.filter(o => {
      const matcher = o.matcher.toUpperCase();
      const queryUpper = query.toUpperCase();
      return matcher.indexOf(queryUpper) > -1;
    });
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    if (onChange) {
      onChange(newQuery);
    }
  };

  const handleSelect = (o: FilterableSelectorOption | null) => {
    setIsOpen(false);
    onSelect(o);
  };

  const filteredOptions = getFilteredOptions();
  const unselectedLabel = _.isNil(isRequired)
    ? '選択してください'
    : isRequired
    ? '(必須)選択してください'
    : '(任意)選択してください';

  const grayBorderColor = '#eee';
  const fieldBgColor = '#fff';

  return (
    <div
      ref={selectAreaRef}
      style={{ width: '100%', fontSize: '13px', marginBottom: '5px', lineHeight: 'normal' }}
    >
      <div
        data-testid="fs_open_box"
        role="button"
        tabIndex={0}
        onClick={toggleOpen}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
        style={{
          display: 'flex',
          border: `2px solid ${grayBorderColor}`,
          padding: '0px 10px',
          cursor: 'pointer',
          lineHeight: '30px',
          whiteSpace: 'normal',
          wordBreak: 'break-all'
        }}
      >
        <div>{selected ? selected.label : unselectedLabel}</div>
        <div style={{ marginLeft: 'auto' }}>{isOpen ? '▲' : '▼'}</div>
      </div>
      {isOpen && (
        <div style={{ margin: 0, background: fieldBgColor }}>
          <div style={{ marginTop: '5px' }}>
            <input
              type="text"
              id="filterable-selector-input"
              placeholder={placeholder}
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              style={{ marginBottom: 0 }}
            />
          </div>
          <ul
            style={{
              overflowY: 'scroll',
              maxHeight: '150px',
              width: workflowFormWidth || '420px',
              borderBottom: `2px solid ${grayBorderColor}`
            }}
          >
            <li
              tabIndex={0}
              onClick={() => handleSelect(null)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(null);
                }
              }}
              style={{
                background: fieldBgColor,
                minHeight: '30px',
                width: '100%',
                lineHeight: '30px',
                padding: '0 10px',
                cursor: 'pointer',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                border: `2px solid ${grayBorderColor}`,
                borderTop: 'none',
                zIndex: 100
              }}
            >
              選択を外す
            </li>
            {filteredOptions.map(o => (
              <li
                key={o.value}
                tabIndex={0}
                onClick={() => handleSelect(o)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(o);
                  }
                }}
                style={{
                  background: fieldBgColor,
                  minHeight: '30px',
                  width: '100%',
                  lineHeight: '30px',
                  padding: '0 10px',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  border: `2px solid ${grayBorderColor}`,
                  borderTop: 'none',
                  zIndex: 100
                }}
              >
                {o.label}
                {o.labelLines?.map((line, i) => (
                  <React.Fragment key={`${o.value}-line-${i}`}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SelectProjects;
